import angular from 'angular';
import {vrmConfigModule} from '@/angular/modules/vrm-config';
import {highchartsLazyloadModule} from '@/angular/libs/highcharts-ng-lazyload';
import {vrmLocalesModule} from '@/angular/modules/vrm.locales';
import {localeEvents} from '@/angular/modules/vrm.locales';
import {defaultsDeep, find} from 'lodash';
import 'moment';
import {
    ALL_Y_AXIS_SCALES,
    INDIVIDUAL_Y_AXIS_SCALES,
    DEFAULT_Y_AXIS_SCALES,
    LEFT_MARGIN_AXES,
    RIGHT_MARGIN_AXES,
    OPPOSITE_AXES
} from "@/enums/custom-widget-axis-customisation";
import { getValueFormatter, getValueFormatterWithMeta, formatSecondsToReadableTime } from '@/angular/utils/graphUtils';
import moment from "moment-timezone";

export const vrmGraphModule = angular.module('vrm.graph', [
    vrmConfigModule.name,
    highchartsLazyloadModule.name,
    vrmLocalesModule.name,
]);

const dynamicConfigs = require.context('./configs/', true, /\.js/);
dynamicConfigs.keys().forEach(moduleDescription => dynamicConfigs(moduleDescription));

const ZEROLINE_OPACITY_DEFAULT = 1.0;
const ZEROLINE_OPACITY_FADED = 0.2;

class GraphService {
    highcharts = null;
    promise = null;

    /* @ngInject */
    constructor(
        WhitelabelService,
        $rootScope,
        $q,
        ApiService,
        locale,
        highchartsNG,
        $filter,
        $timeout,
        advancedGraph,
        highchartsOptions,
        moment,
        AppearanceService,
        VrmLangService
    ) {
        this.WhitelabelService = WhitelabelService;
        this.$rootScope = $rootScope;
        this.$q = $q;
        this.ApiService = ApiService;
        this.locale = locale;
        this.highchartsNG = highchartsNG;
        this.$filter = $filter;
        this.$timeout = $timeout;
        this.advancedGraph = advancedGraph;
        this.highchartsOptions = highchartsOptions;
        this.moment = moment;
        this.AppearanceService = AppearanceService;
        this.VrmLangService = VrmLangService;

        this.VrmLangService.getLang().then(lang => {
            this.currentLanguage = lang;
        });

        // Reload on whitelabel change
        $rootScope.$on('whitelabelChanged', () => {
            if (this.highcharts) {
                this.invalidateGraphConfigs();
            }
        });

        const usedLocales = ['common', 'graph'];
        this.promise = $q.all([highchartsNG.getHighcharts(), locale.ready(...usedLocales)]).then((result) => {
            this.highcharts = result[0];
            this.onHighchartsLoaded();
            // If the locale changes in the future, then invalidate the configs
            $rootScope.$on(localeEvents.localeChanges, () => {
                locale.ready(...usedLocales).then(() => {
                    this.invalidateGraphConfigs();
                });
            });
        });
    }

    onHighchartsLoaded() {
        this.highcharts.setOptions(this.highchartsOptions());
    }

    invalidateGraphConfigs() {
        this.onHighchartsLoaded();
        this.$rootScope.$emit('graphConfigsInvalidated');
    }

    addVirtualFields(graphSettings) {
        const fields = graphSettings ? {...graphSettings.fields} : {};
        // inject virtual fields
        const { virtualFields } = graphSettings || {};
        if (virtualFields) {
            for (let virtualField in virtualFields) {
                fields[virtualField] = virtualFields[virtualField].fieldOptions;
            }
        }
        return fields;
    }
    /**
     * Create graph config for graph on advanced page
     * @param graphSettings entry from AdvancedGraphWidgetConfigs
     * @param colors Colors to override
     * @returns {graph}
     */
    getAdvancedConfig(graphSettings, colors, lineColors) {
        const fields = graphSettings ? graphSettings.fields : {};

        const options = graphSettings ? graphSettings.extraOptions : {} ;
        const advanced = this.advancedGraph();
        const config = options ? defaultsDeep({}, options, advanced) : advanced;
        if (colors) {
            config.options.colors = colors;
        }

        // Generate series
        // TODO: This may break, order for plain javascript objects is not guaranteed!
        Object.keys(fields).forEach((code, index) => {
            const field = fields[code] || {};
            const dataSeries = {
                yAxis: field.yAxis || 0,
                lineWidth: 1,
                lineColor: lineColors ?  this.WhitelabelService.getGraphColors()[lineColors[index]] : config.options.colors[index],
                visible: code !== 'TOTAL', // Total series is hidden by default
                // Add hover effect for series
                states: {
                    hover: {
                        lineWidth: 2,
                        lineWidthPlus: 1
                    }
                },

                // Add hover (animated) effect for zero-lines
                events: {
                    mouseOver(event) {
                        event.target.yAxis.plotLinesAndBands.forEach((el) => {
                            el.svgElem.animate({
                                'opacity': ZEROLINE_OPACITY_DEFAULT,
                            }, false);
                        });
                        event.target.chart.yAxis.forEach(axis => {
                            if (axis !== event.target.yAxis) {
                                axis.plotLinesAndBands.forEach((el) => {
                                    el.svgElem.animate({
                                        'opacity': ZEROLINE_OPACITY_FADED,
                                    }, true);
                                });
                            }
                        });
                    },
                    mouseOut(event) {
                        event.target.yAxis.plotLinesAndBands.forEach((el) => {
                            el.svgElem.animate({
                                'opacity': ZEROLINE_OPACITY_DEFAULT,
                            }, true);
                        });
                    },
                },
            };
            if (field.serieOptions) {
                angular.merge(dataSeries, field.serieOptions);
            }
            config.series.push(dataSeries);

        });
        return config;
    }

    getTimeInTimezone(x) {
        let offset = 0;
        if (this.highcharts.getOptions().global.timezoneOffset) {
            offset = -60 * 1000 * this.highcharts.getOptions().global.timezoneOffset;

            if (this.highcharts.getOptions().global.timezone) {
                // if string value of timezone is st check if there was a daylight savings switch between current date
                // and date we are converting (in that case timezoneOffset is only valid for today, not for the date
                // to convert)
                const zone = moment.tz.zone(
                    this.highcharts.getOptions().global.timezone
                );
                const daylightSavingsDIff =
                    (zone.parse(Date.now()) -
                        zone.parse(new Date(x).getTime())) *
                    60 *
                    1000;
                offset += daylightSavingsDIff;
            }
        }
        let date = new Date(x + offset);
        date.setTime(date.getTime() + date.getTimezoneOffset() * 60 * 1000);
        return date;
    }

    getCurrencySign() {
        return this.highcharts.getOptions().global.currencySign;
    }

    getTooltipFormatterForGraphWidget() {
        const self = this;
        return {
            useHTML: true,
            headerFormat: '<table>',
            footerFormat: '</table>',
            formatter: function (tooltip) {
                const items = this.points || self.highcharts.splat(this);
                const timestamp = self.$filter('date')(self.getTimeInTimezone(items[0].x), 'yyyy-MM-dd HH:mm');

                return [
                    tooltip.tooltipFooterHeaderFormatter(items[0]),
                    `<tr><td class="highcharts-tooltip__title" colspan="2">${timestamp}</td></tr>`,
                    ...tooltip.bodyFormatter(items),
                    tooltip.tooltipFooterHeaderFormatter(items[0], true)
                ];
            }
        }
    }

    getTablePointFormatter({range=false, overrideColor=null, striped=false, customValueFormatter=null} = {}) {
        const self = this;
        return function () {
            const color = overrideColor ? overrideColor : this.series.color;

            const tooltip = this.series.options.tooltip;
            const prefix = tooltip.valuePrefix;
            const suffix = tooltip.valueSuffix;
            const digits = tooltip.valueDecimals;
            let valueFormatter = getValueFormatter(prefix, suffix, digits);
            if (customValueFormatter){
                valueFormatter = customValueFormatter;
            }

            const isHovered = this.series === this.series.chart.hoverPoint.series;
            const formattedValue = range ? valueFormatter(this.low) + ' - ' + valueFormatter(this.high) : valueFormatter(this.y);

            let rows = self.getTablePointFormat(color, this.series.name, formattedValue, isHovered, striped)

            // Custom extra rows for a single datapoint
            const extraRows = this.options.extraTooltipRows;
            if (extraRows) {
                extraRows.forEach(extraRow => {
                    rows += self.getTablePointFormat(color, extraRow.name, extraRow.value, isHovered, striped)
                })
            }

            return rows

          }
    }

    getTablePointFormat(color, label, formattedValue, bold, striped){
        // Check if `formattedValue` is a string that represents seconds,
        // e.g., "5s", "  12.5s", or "0s" !m/s is not considered
        if (typeof formattedValue === 'string' && /^\s*\d+(\.\d+)?\s*s$/.test(formattedValue.trim())) {
            const numericPart = parseFloat(formattedValue);
            if (!isNaN(numericPart)) {
                formattedValue = formatSecondsToReadableTime(numericPart, this.currentLanguage);
            }
        }
        let checkboxStyle = `background-color:${color};border-color:${color}`
        if (striped){
            checkboxStyle =
            `
                background: repeating-linear-gradient(
                    135deg,
                    ${color},
                    ${color} 1px,
                    transparent 1px,
                    transparent 2px
                );
                border-color: ${color};
            `
        }

        return `<tr style="${(bold ? "font-weight: bold;" : "")}">
                    <td>
                        <span class="highcharts-tooltip__checkbox-item">
                            <span class="highcharts-tooltip__checkbox-item__checkbox" style="${checkboxStyle}"></span>
                            <span>${label}</span>
                        </span>
                    </td>
                    <td style="text-align: right">
                        ${formattedValue}
                    </td>
                </tr>`
    }

    /**
     * Generated a total entry in the tooltip table by computing the total for the given series.
     * @param {*} that - the context (formatter) where it is executed, used as a pointer to accept data point and series fields.
     * @param {*} series - series for which the total is computed
     */
    generateTotalTableEntry(that, series, unit, totalTranslationKey, forceHideTotal, prefixUnit, rangeCap, firstLine) {
        // Build total based on the series we should sum
        var s = [];
        var total = 0;
        let startDate = null;
        let endDate = null;
        var showTotal = false;
        totalTranslationKey = totalTranslationKey || 'graph.total';

        $.each(that.points, function () {
            if (series.indexOf(this.series.index) > -1) {
                showTotal = true;
                total += this.y;
            }
        });

        const range = that.points[0].series.closestPointRange;
        if (range) {
            const format = range >= (60 * 60 * 24 * 1000) ? 'yyyy-MM-dd HH:mm' : 'HH:mm';

            if (range > 27 * 60 * 60 * 24 * 1000) {
                startDate = this.moment(that.points[0].x);
                endDate = this.moment(that.points[0].x).endOf("month");
                if (endDate.diff(startDate) < 86400000) {
                    startDate.add(1, "M");
                    endDate.add(1, "M");
                }
                startDate = startDate.startOf("month").toDate();
                endDate = endDate.toDate();

                // Patch start or end dates if outside of range cap
                if (
                    rangeCap &&
                    rangeCap[0] &&
                    startDate < new Date(rangeCap[0])
                ) {
                    startDate = new Date(rangeCap[0]);
                }
                if (
                    rangeCap &&
                    rangeCap[1] &&
                    endDate > new Date(rangeCap[1])
                ) {
                    endDate = new Date(rangeCap[1]);
                }
            } else {
                startDate = that.points[0].x;
                endDate = startDate + range - 1000; // Remove one second to make clear it's not inclusive

                // Patch start or end dates if outside of range cap
                if (rangeCap && rangeCap[0] && startDate < rangeCap[0]) {
                    startDate = rangeCap[0];
                }
                if (rangeCap && rangeCap[1] && endDate > rangeCap[1]) {
                    endDate = rangeCap[1];
                }
                startDate = this.getTimeInTimezone(startDate);
                endDate = this.getTimeInTimezone(endDate);
            }
            startDate = this.$filter("date")(startDate, format);
            endDate = this.$filter("date")(endDate, format);

            if (startDate && endDate) {
                if(!firstLine){
                    s.push('<tr><td class="highcharts-tooltip__title highcharts-tooltip__time" colspan="2"><b>' + startDate + ' - ' + endDate + '</b></td></tr>');
                }
                else{
                    s.push('<tr><td class="highcharts-tooltip__title highcharts-tooltip__time-first" colspan="2"><b>' + startDate + ' - ' + endDate + '</b></td></tr>');
                }
            }
        }

        // Add the total
        if (showTotal && (typeof forceHideTotal == 'undefined' || !forceHideTotal)){
            const formattedValue = (prefixUnit ? unit : '') + parseFloat(Math.round(total * 100) / 100).toFixed(2) + (prefixUnit ? '' : (' ' + unit));
            s.push(this.getTableTotalFormat(formattedValue, totalTranslationKey));
        }

        return s;
    }

    getTableTotalFormat(formattedValue, totalTranslationKey='graph.total'){
        return `<tr>
                    <td class='highcharts-tooltip__total' ><b>${this.locale.getString(totalTranslationKey)}</b></td>
                    <td class='highcharts-tooltip__total__figure' style="text-align: right">
                        <b>${formattedValue}</b>
                    </td>
                </tr>`
    }

    /**
     * A tabular formatter with multiple total fields and grouped items.
     */
    getTooltipFormatterWithMultipleTotals(totalLabels, seriesArr, forceHideTotal) {
        const self = this;
        return {
            useHTML: true,
            headerFormat: '<table>',
            footerFormat: '</table>',
            formatter: function (tooltip) {
                var items = this.points || self.highcharts.splat(this), s;

                // Build the header
                s = [tooltip.tooltipFooterHeaderFormatter(items[0])];

                // compute a grouped total and add the values for each group
                for (var index in seriesArr) {
                    // add the total

                    if(index == 0 ){
                        s = s.concat(self.generateTotalTableEntry(this, seriesArr[index], self.getCurrencySign(), totalLabels[index], forceHideTotal, null, null ,true));
                    }
                    else{
                        s = s.concat(self.generateTotalTableEntry(this, seriesArr[index], self.getCurrencySign(), totalLabels[index], forceHideTotal));
                    }

                    $.each(items, function () {
                        if (seriesArr[index].indexOf(this.series.index) > -1) {
                            s = s.concat(tooltip.bodyFormatter([this]));
                        }
                    });
                }

                // footer
                s.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));
                return s;
            }
        }
    }

    /**
     * A tabular formatter with a Total field that sums as the specified series.
     */
    getTooltipFormatterWithTotal(series, unit, forceHideTotal) {
        const self = this;
        return {
            useHTML: true,
            headerFormat: '<table>',
            footerFormat: '</table>',
            formatter: function (tooltip) {
                var items = this.points || self.highcharts.splat(this), s;
                var rangeCap = tooltip.options.rangeCap || [];

                // build the header
                s = [tooltip.tooltipFooterHeaderFormatter(items[0])];

                // add the total field
                s = s.concat(self.generateTotalTableEntry(this, series, unit, 'graph.total', forceHideTotal, false, rangeCap));

                // build the values (all of them, not only the ones used in Total)
                s = s.concat(tooltip.bodyFormatter(items));

                // footer
                s.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));
                return s;
            }
        }
    }
    getLiveFeedTooltipFormatter(series, unit, forceHideTotal) {
        const self = this;
        return {
            useHTML: true,
            headerFormat: '<table>',
            footerFormat: '</table>',
            formatter: function (tooltip) {
                var items = this.points || self.highcharts.splat(this), s;
                var rangeCap = tooltip.options.rangeCap || [];

                items = items.filter(item => item !== null);
                if (!items || items.length < 1) return false;

                // build the header
                s = [tooltip.tooltipFooterHeaderFormatter(items[0])];

                // add the total field
                s = s.concat(self.generateTotalTableEntry(this, series, unit, 'graph.total', forceHideTotal, false, rangeCap));

                // build the values (all of them, not only the ones used in Total)
                s = s.concat(tooltip.bodyFormatter(items));

                // footer
                s.push(tooltip.tooltipFooterHeaderFormatter(items[0], true));
                return s;
            }
        }
    }

    getTooltipPositionStateGraph(labelWidth, labelHeight, point) {
        const pX = point.plotX;
        const chartWidth = this.chart.plotWidth;
        const y = point.plotY - labelHeight / 2;
        if (chartWidth > pX + labelWidth && pX > 0) {
            return { x: pX + labelWidth / 4, y };
        } else if (pX < labelWidth && labelWidth * 5 / 4 < chartWidth) {
            return { x: labelWidth / 4, y };
        }
        return { x: chartWidth - labelWidth * 0.75, y };
    }

    reversedStatePointFormatter() {
        const self = this;
        return function () {
            var format = 'yyyy-MM-dd HH:mm';
            return '<span style="color:' + this.color + '">\u25CF</span> ' + this.series.name + ', <b>' + self.$filter('date')(self.getTimeInTimezone(this.low), format) + ' - ' + self.$filter('date')(self.getTimeInTimezone(this.high), format) + '</b>';
        }
    }

    statePointFormatter() {
        const self = this;
        return function () {
            var format = 'yyyy-MM-dd HH:mm';
            return '<b>' + self.$filter('date')(self.getTimeInTimezone(this.low), format) + ' - ' + self.$filter('date')(self.getTimeInTimezone(this.high), format) + '</b>';
        }
    }

    stateAxisLabelFormatter() {
        return function () {
            return `<div data-tooltip="${this.value}" style="margin-right:-13px; overflow-x:hidden; white-space:nowrap; text-overflow:ellipsis;">${this.value} &nbsp;</div>`;
        }
    }

    datetimeLabeler() {
        const self = this;
        return function () {
            if (this.isFirst && this.axis.isDatetimeAxis && this.dateTimeLabelFormat.startsWith('%H:%M')) {
                var format = '%e. %b';
                var date = new Date(this.value);
                var isMidnight = (date.getHours() === 0) && (date.getMinutes() === 0);
                // If the timestamp is not at midnight then append the time
                if (!isMidnight) {
                    format += ' ' + this.dateTimeLabelFormat;
                }
                return self.highcharts.dateFormat(format, this.value);
            }
            return this.axis.defaultLabelFormatter.call(this);
        }
    }

    /**
     * Update config after retrieving data from Graph api
     * @param config config retrieved from getAdvancedConfig
     * @param dataSource data received from api
     * @param attributes meta received from api
     * @param meta field data from AdvancedGraphWidgetConfigs
     * @param allowTotal whether total serie should be added
     * @param totalOnly show only the total series and hide the components
     * @param range Current rage
     * @param preprocessValue Function that preprocesses a data point
     * @param useMinMax Use the minmax-range instead of the avarage display
     * @param yAxisProperties configuration of the range that should be shown on the y-axis
     */
    updateConfig(config, dataSource, attributes, meta, allowTotal, totalOnly, range, preprocessValue, useMinMax, yAxisProperties) {
        allowTotal = !!allowTotal;
        totalOnly = !!totalOnly;
        config.loading = false;
        if (!preprocessValue) {
            preprocessValue = value => value;
        }

        const updateSeriesSettings = (series, meta, pointFormatterColor = null) => {
            const supportsMinMax = series.type !== 'column';
            if (supportsMinMax) {
                series.type = useMinMax ? 'arearange' : 'line';
            }
            const valueFormatter = getValueFormatterWithMeta(meta);
            const isRange = supportsMinMax && useMinMax
            series.tooltip = {
                pointFormatter: this.getTablePointFormatter({range: isRange, overrideColor: pointFormatterColor, customValueFormatter: valueFormatter}),
            };
        }


        const series = config.series;
        let seriesCounter = 0;
        const total = {};
        let lastId = null;
        const negativeNumberAdded = new Set();
        const foundNonZeroData = new Set();
        const dataFound = new Set();
        let forecastData = false;
        let metaAndSeriesMismatch = false;

        const LINE_CHART_MINIMUM_ATTRIBUTES = ['mcV'];
        const LINE_CHART_MAXIMUM_ATTRIBUTES = ['McV'];

        Object.keys(meta).forEach(code => {
            if (metaAndSeriesMismatch) {
                return;
            }
            //Find id of meta code
            const id = Object.keys(attributes).find(k => {
                return (attributes[k].code === code);
            });

            if (code === 'vrm_solar_irradiance_fc')
                forecastData = true;

            if (!id) {
                return;
            }
            const currentSeries = series[seriesCounter];

            if (Object.keys(meta).length !== series.length) {
                metaAndSeriesMismatch = true;
                return;
            }
            lastId = id;
            currentSeries.name = attributes[id].description;
            currentSeries.data = [];
            updateSeriesSettings(currentSeries, attributes[id], currentSeries.lineColor);

            function renderLineChart(graphEntry) {
                const [
                    x,
                    average = null,
                    min,
                    max,
                    ...extraColumns
                ] = graphEntry;
                const key = x * 1000;

                let chosenValue = average;
                if (LINE_CHART_MINIMUM_ATTRIBUTES.includes(code)){
                    chosenValue = min;
                }
                if (LINE_CHART_MAXIMUM_ATTRIBUTES.includes(code)){
                    chosenValue = max;
                }
                const value = preprocessValue(chosenValue, x, attributes[id].code, dataSource, attributes);

                currentSeries.color = currentSeries.lineColor;
                // Do not add data entries with null value because they will break the appearance of columns
                if (value === null  && currentSeries.type === 'column') return;

                currentSeries.data.push({x: key, y: value, extraTooltipRows: getExtraTooltipRows(code, extraColumns)});
                // Keep track if a negative number has been added to the data
                if (value < 0) {
                    negativeNumberAdded.add(currentSeries.yAxis);
                }
                // Keep track if a non zero data entry is found
                if (value !== 0) {
                    foundNonZeroData.add(currentSeries.yAxis);
                }
                // Keep track if data is found per axis
                dataFound.add(currentSeries.yAxis);

                if (allowTotal) {
                    const hasData = average !== null;
                    const totalEntry = total[key] || (total[key] = [null]);
                    if (hasData) {
                        totalEntry[0] += value;
                    }
                }
            }

            function renderRangeChart(graphEntry) {
                const [
                    x,
                    average = null,
                    min = null,
                    max = null
                ] = graphEntry;
                const key = x * 1000;

                // Ensure range chart is non-empty when line chart is non-empty
                const minOrAverage = min != null ? min : average;
                const maxOrAverage = max != null ? max : average;
   
                const minValue = preprocessValue(minOrAverage, x, attributes[id].code, dataSource, attributes);
                const maxValue = preprocessValue(maxOrAverage, x, attributes[id].code, dataSource, attributes);
                currentSeries.data.push([key, minValue, maxValue]);

                // Keep track if a negative number has been added to the data
                if (minOrAverage < 0) {
                    negativeNumberAdded.add(currentSeries.yAxis);
                }
                // Keep track if a non zero data entry is found
                if (minOrAverage !== 0 || maxOrAverage !== 0) {
                    foundNonZeroData.add(currentSeries.yAxis);
                }
                // Keep track if data is found per axis
                dataFound.add(currentSeries.yAxis);

                if (allowTotal) {
                    const hasData = minOrAverage !== null && maxOrAverage !== null;
                    const totalEntry = total[key] || (total[key] = [null, null]);
                    if (hasData) {
                        totalEntry[0] += minValue;
                        totalEntry[1] += maxValue;
                    }
                }
            }

            const getExtraTooltipRows = (code, extraColumns) => {
                if (extraColumns.length === 0 || extraColumns[0] === 0) return [];

                // Show minimum and maximum cell id if value exists (not zero)
                const extraTooltipRows = [];

                switch (code){
                    case 'mcV':
                        extraTooltipRows.push({ name: "Minimum voltage cell ID", value: extraColumns[0] });
                        break;
                    case 'McV':
                        extraTooltipRows.push({ name: "Maximum voltage cell ID", value: extraColumns[0] });
                        break;
                    case 'mcT':
                        extraTooltipRows.push({ name: "Minimum temperature cell ID", value: extraColumns[0] });
                        break;
                    case 'McT':
                        extraTooltipRows.push({ name: "Maximum temperature cell ID", value: extraColumns[0] });
                        break;
                }

                return extraTooltipRows;
            }

            const yAxis = config.yAxis[currentSeries.yAxis];
            const points = dataSource[id];
            points.forEach(useMinMax ? renderRangeChart : renderLineChart);

            const setYAxisOptions = ({ min = 0, max = 0, interval = 30 }) => {
                let nonZeroInterval = interval || 1;
                const step = (max - min) / nonZeroInterval;
                const tickPositions = (new Array(interval + 1).fill(undefined)).map((_, idx) => +(min + idx * step).toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/,'$1'));
                yAxis.minTickInterval = 0.1;
                yAxis.max = max;
                yAxis.min = min;
                yAxis.tickAmount = interval;
                yAxis.endOnTick = false;
                yAxis.startOnTick = false;
                yAxis.tickPositions = tickPositions;
            }

            // apply initial configurations of the yAxis properties
            if(yAxisProperties && yAxisProperties.method) {
                delete yAxis.max;
                delete yAxis.min;
                delete yAxis.tickInterval;
                delete yAxis.endOnTick;
                delete yAxis.startOnTick;
                delete yAxis.tickPositions;
                yAxis.minTickInterval = 0.1;
                yAxis.tickAmount = 10;
                switch(yAxisProperties.method) {
                    case(INDIVIDUAL_Y_AXIS_SCALES):
                        const codeProps = yAxisProperties.attributeProperties ? yAxisProperties.attributeProperties[code] : null;
                        setYAxisOptions({
                            min: codeProps ? +codeProps.min : 0,
                            max: codeProps ? +codeProps.max : 0,
                            interval: codeProps ? +codeProps.interval : 30,
                        });
                        break;
                    case(ALL_Y_AXIS_SCALES):
                        setYAxisOptions({
                            min: +yAxisProperties.globalMin || 0,
                            max: +yAxisProperties.globalMax || 0,
                            interval: +yAxisProperties.globalInterval || 30,
                        });
                        break;
                    case(DEFAULT_Y_AXIS_SCALES):
                        if(code.startsWith("bs_")  || code.startsWith("SOC_") ){
                            setYAxisOptions({
                                min: 0,
                                max:  100,
                                interval: 10,
                            });
                        }
                        break;
                }
            }

            seriesCounter++;
        });

        if (metaAndSeriesMismatch) {
            return -1;
        }

        // Build total row if necessary
        if (allowTotal && series.length >= 1 && series[seriesCounter]) {
            const totalSeries = series[seriesCounter];
            totalSeries.name = 'Total';
            totalSeries.data = [];
            totalSeries.color = totalSeries.lineColor;
            updateSeriesSettings(totalSeries, attributes[lastId], totalSeries.lineColor);

            _.each(total, function (value, key) {
                value = Array.isArray(value) ? value : [value];
                totalSeries.data.push([parseInt(key), ...value]);
            });

            // Sort total row by timestamp, because it can be a summation of order series
            // correct timestamp order is not guaranteed. And this solves that (Highcharts needs ordered data)
            totalSeries.data = _.sortBy(totalSeries.data, num => num[0]);
        }

        // Set range
        config.xAxis.min = (range ? range.startDate : 1) * 1000;
        config.xAxis.max = (range ? range.endDate : 1) * 1000;

        if (totalOnly) {
            // Make the other series not visible
            _.each(series, function (value, key) {
                series[key].visible = false;
            });

            // And the total (always last "seriesCounter") invisible
            series[seriesCounter].visible = true;
        }

        // Update zeroline configs
        const ensurePlotLine = (axis) => {
            if (!axis.plotLines) {
                axis.plotLines = [{
                    value: 0,
                    color: this.colors.black,
                    width: 1,
                    zIndex: 2,
                    dashStyle: 'Dash',
                    opacity: 0.2,
                }];
            }
        };

        config.yAxis.forEach((axis, index) => {
            ensurePlotLine(axis);
            if (config.series.filter(s => s.visible !== false && s.yAxis === index).length > 0) {
                axis.plotLines.forEach(p => p.color = this.colors.black);
            } else {
                axis.plotLines.forEach(p => p.color = 'transparent');
            }
        });

        // group yAxis for custom widgets
        if (yAxisProperties && yAxisProperties.method) {
            this.setYAxisConfigsForGroups(config, attributes, yAxisProperties);
        }

        config.series = series;
        if (forecastData)
            this.setConfigForecastGraph(config);
    }

    setYAxisConfigsForGroups(config, parsedMetas, yAxisProperties) {
        const parsedEntries = Object.entries(parsedMetas);
        switch (yAxisProperties.method) {
            case ALL_Y_AXIS_SCALES:
                // show yAxis if at least one serie is visible
                config.yAxis[0].visible = !!config.series.some(s => s.visible);
                for (let i = 1; i < parsedEntries.length; i++) {
                    // inherit first axis since that is the default one
                    config.series[i].yAxis = 0;
                    config.yAxis[i].visible = false;
                }
                config.options.chart.marginLeft = LEFT_MARGIN_AXES[1];
                config.options.chart.marginRight =
                    RIGHT_MARGIN_AXES[1];
                break;
            case DEFAULT_Y_AXIS_SCALES:
                for (let i = 0; i < parsedEntries.length; i++) {
                    config.yAxis[i].visible = config.series[i].visible;
                }
                const foundNullAxisGroup = find(
                    parsedEntries,
                    entry => entry[1].axisGroup == null
                );
                const savedAxisGroups = new Map();
                const groupsBasedOnParent = new Map();
                let axisCount = parsedEntries.length;
                if (!foundNullAxisGroup) {
                    for (let i = 0; i < parsedEntries.length; i++) {
                        const meta = parsedEntries[i][1];
                        if (savedAxisGroups.has(meta.axisGroup)) {
                            axisCount--;
                            config.yAxis[i].visible = false;
                            // inherit properties from the parent axis
                            const parent = savedAxisGroups.get(meta.axisGroup)
                            config.series[i].yAxis = parent;
                            const group = groupsBasedOnParent.get(parent);
                            groupsBasedOnParent.set(parent, [...group, i]);
                        } else {
                            savedAxisGroups.set(meta.axisGroup, i);
                            groupsBasedOnParent.set(i, []);
                            config.yAxis[i].opposite =
                                OPPOSITE_AXES[savedAxisGroups.size];
                        }
                    }

                    // set correct visibility based on dependent series
                    for (const [parent, values] of groupsBasedOnParent) {
                        config.yAxis[parent].visible = [parent, ...values].some(s => config.series[s].visible);
                    }

                    config.options.chart.marginLeft =
                        LEFT_MARGIN_AXES[axisCount];
                    config.options.chart.marginRight =
                        RIGHT_MARGIN_AXES[axisCount];
                }
                break;
            case INDIVIDUAL_Y_AXIS_SCALES:
                for (let i = 0; i < parsedEntries.length; i++) {
                    config.yAxis[i].visible = config.series[i].visible;
                }

                config.options.chart.marginLeft =
                    LEFT_MARGIN_AXES[parsedEntries.length];
                config.options.chart.marginRight =
                    RIGHT_MARGIN_AXES[parsedEntries.length];
                break;
        }
    }

    setConfigForecastGraph(config) {
        // Set range
        delete config.xAxis.max;
        config.yAxis.max += config.yAxis.max;
        config.xAxis.plotBands = [
            {
                color: this.AppearanceService.isDarkModeActive() ? '#373737' : '#FAF9F5',
                from: Date.now(),
                to: Number.MAX_VALUE
            }
        ];
        config.xAxis.plotLines = [
            {
                color: this.colors.grey,
                value: Date.now(),
                width: 1,
                label: {
                    text: this.locale.getString('advanced.now'),
                    rotation: 0,
                    x: -12,
                    y: -10,
                    style: {
                        color: this.colors.grey
                    }
                }
            }
        ];
        config.series[1].dashStyle = 'dash';
        config.series[1].marker = {
            enabled: true,
            symbol: "circle",
            radius: 2.5
        };
        config.options.chart.marginTop = 25;
    }



    /**
     * Update config after retrieving data from an state graph api
     * @param config config retrieved from getConfig(GraphConfigTypes.advancedReversedState)
     * @param data received from api
     * @param range date range of graph in unix timestamp
     * @param meta field data from AdvancedGraphWidgetConfigs
     * @param ignore list of ignored enum id's
     * @param showOnlyWithData boolean whether to show only the categories with data
     */
    updateReversedStateConfig(config, data, range, meta, ignore, showOnlyWithData) {
        config.loading = false;
        var series = config.series;

        var categoryMap = {};
        var categories = [];

        //Empty series data
        for (var index in series) {
            series[index].data = [];
        }

        function _findId(code) {
            var id = null;
            _.each(_.keys(data.meta), function (k) {
                if (data.meta[k].code === code)
                    id = k;
            });
            return id;
        }

        for (var code in meta) {
            //Find id of meta code
            var id = _findId(code);

            if (id) {
                //Create series from enums
                for (let index in data.enums[id]) {
                    if (!series[index]) {
                        series.push({
                            animation: false,
                            visible: true,
                            name: data.enums[id][index],
                            data: [],
                            stack: 'Main'
                        });
                    }
                }


                //Create category
                if (!categoryMap[id]) {
                    categoryMap[id] = categories.length;
                    // If showOnlyWithData is set to false, a category is shown regardless if there is data for it
                    if (!showOnlyWithData){
                        categories.push(data.meta[id].description);
                    }
                }

                //Convert data to correct format
                if(data.data && data.data[id]){
                    // Boolean that stores whether there is any data that we do not ignore for this category
                    let hasData = false;
                    for (const [key, block] of Object.entries(data.data[id])) {
                        if (!_.includes(block[0], ignore)) {
                            // If there is any data that we do not ignore, set this boolean to true
                            hasData = true;
                            series[block[0]].data.push({
                                x: categoryMap[id],
                                low: block[1]*1000,
                                high: block[2]*1000
                            });
                        }
                    }
                    // If showOnlyWithData is true, we consider only categories with data that we do not ignore
                    if (showOnlyWithData && hasData){
                        categories.push(data.meta[id].description);
                    }
                }
            }
        }

        // Set range
        config.yAxis.min = range.startDate * 1000;
        config.yAxis.max = range.endDate * 1000;
        config.yAxis.crosshair = {width: 1};

        config.xAxis.min = 0;
        config.xAxis.max = Object.keys(categories).length - 1;
        config.xAxis.categories = categories;
        config.series = series;
    }

    /**
     * Update config after retrieving data from an state graph api
     * @param config config retrieved from getConfig(GraphConfigTypes.advancedState)
     * @param data received from api
     * @param range date range of graph in unix timestamp
     * @param meta field data from AdvancedGraphWidgetConfigs
     */
    updateStateConfig(config, data, range, meta) {
        config.loading = false;
        var series = config.series;

        var i = 0;
        for (var code in meta) {
            //Find id of meta code
            var id = null;
            _.each(_.keys(data.meta), function (k) {
                if (data.meta[k].code === code)
                    id = k;
            });

            // Map categories, highcharts needs numeric index starting at 0
            var categories = {};
            var catCount = 0;
            var categoryMap = {};
            var arrEnums = [];

            // Sort enums by their sort order
            _.each(data.enums[id], function (enumName, enumId) {
                arrEnums.push({name: enumName, id: enumId});
            });
            arrEnums = arrEnums.sort(function (a, b) {
                if (data['sort'][id][a.id] == data['sort'][id][b.id]) return 0;
                return (data['sort'][id][a.id] < data['sort'][id][b.id]) ? -1 : 1;
            });
            _.each(arrEnums, function (curEnum) {
                var existingEnumCounter = _.invert(categories)[curEnum.name];
                if (typeof existingEnumCounter === 'undefined') {
                    categories[catCount] = curEnum.name;
                    categoryMap[curEnum.id] = catCount;
                    catCount++;
                } else {
                    categoryMap[curEnum.id] = parseInt(existingEnumCounter);
                }
            });

            //Use first enum for states on xAxis
            if (i == 0) {
                config.xAxis.categories = categories;
                config.xAxis.min = 0;
                config.xAxis.max = Object.keys(categories).length - 1;
            }

            //During first time data is load series are created
            if (!series[i]) {
                series.push({
                    animation: false,
                    visible: true
                })
            }

            if (id) {
                series[i].name = data.meta[id].description;

                var curData = [];
                //Convert data to correct format
                if(data.data && data.data[id]){
                    for (const [key, block] of Object.entries(data.data[id])) {
                        let entry = categoryMap[parseInt(block[0])];
                        if (typeof entry !== 'undefined') {
                            curData.push([entry, block[1]*1000, block[2]*1000]);
                        }
                    }
                }


                // Due to state grouping, it can be the case that there are consecutive entries
                // in the same category in the array (e.g. 3 entries for category x after each other)
                // the ending timestamps of those entries lead to small gaps in the state graphs if they are not
                // stitched together. So here all consecutive entries are converted to a single large entry
                var finalData = [];
                var lastCategory = null;
                var lastTime = null
                _.each(curData, function (record) {
                    if (lastCategory === record[0] && lastTime === record[1]) {
                        finalData[finalData.length - 1][2] = record[2];
                    } else {
                        finalData.push(record);
                    }
                    lastCategory = record[0];
                    lastTime = record[2]
                });
                //Sort data based on state for Highchart
                series[i].data = _.sortBy(finalData, 0);
            }
            i++;
        }

        // Set range
        config.yAxis.min = range.startDate * 1000;
        config.yAxis.max = range.endDate * 1000;
        config.yAxis.crosshair = {width: 1};

        config.series = series;
    }

    getGraphData(id, startDate, endDate, interval, url, parameters) {
        var reqParameters = Object.assign({start: startDate, end: endDate, interval }, parameters);
        return this.ApiService.installations(id, url, reqParameters).then(function (response) {
            return response.data;
        });
    }

    getOverallStatsData(id, type) {
        return this.ApiService.installations(id, 'overallstats', {type: type}).then(function (response) {
            return response.data;
        });
    }

    ready() {
        return this.promise;
    }

    registerSyncEventListeners(container) {
        const service = this;
        // Keep track of the currently highlighted points
        let higlightedPoints = [];

        function searchPoint(x, chart) {
            if (!chart || !chart.series.length) return;
            const firstVisibleSeries = _.findIndex(chart.series, 'visible');
            if (firstVisibleSeries < 0) return;
            const points = chart.series[firstVisibleSeries].points;
            if (!points) return;
            return points[_.sortedIndexBy(points, {x}, 'x')];
        }

        function searchPointColumnRange(scrPoint, y, chart) {
            if (!chart || !chart.series.length) return;
            const firstVisibleSeries = _.findIndex(chart.series, 'visible');
            if (firstVisibleSeries < 0) return;
            const points = chart.series[firstVisibleSeries].points;
            if (!points) return;
            let returnPoint;
            let possiblePoints = [];
            //check all bars that cover the mouse location
            chart.series.forEach((serie) =>{
                serie.data.forEach((point) =>{
                    if(point.low < y && point.high >= y){
                        possiblePoints.push(point);
                        if(scrPoint && point.x === scrPoint.x) {
                            returnPoint = point;
                        }
                    }
                })
            })
            //if specific bar is hovered over return this.
            if (returnPoint) return returnPoint;

            //If no bars are found check if there is one within 1 minute (
            //so that states of seconds will still be shown)
            const oneMinute = 60000;
            if (possiblePoints.length === 0) {
                chart.series.forEach((serie) =>{
                    serie.data.forEach((point) =>{
                        if(point.low- oneMinute< y && point.high + oneMinute >= y){
                            possiblePoints.push(point);
                        }
                    })
                })
            }

            //return the closest/tightest horizontal bar to the mouse cursor given the
            let minimalDistance = Number.MAX_VALUE;
            possiblePoints.forEach((point) =>{
                const currentDistance = Math.abs(y - point.low) + Math.abs(point.high - y);
                if(currentDistance < minimalDistance){
                    minimalDistance = currentDistance;
                    returnPoint = point;
                }
            })
            return returnPoint
        }

        function highlight(e) {
            if (!service.highcharts) return;
            if (!e.currentTarget || !e.currentTarget.children[0]) return;
            const srcChartIndex = +e.currentTarget.children[0].getAttribute('data-highcharts-chart');
            const srcChart = service.highcharts.charts[srcChartIndex]
            const event = srcChart.pointer.normalize(e);
            if (!srcChart.series.length) return;
            const firstVisibleSeries = _.findIndex(srcChart.series, 'visible');
            if (firstVisibleSeries < 0) return;
            const srcChartPoint = srcChart.series[firstVisibleSeries].searchPoint(event, true);
            let srcTimestamp;

            //Incase the graph is a state graph the yAxis value should be used to get the timestamp
            if(srcChart.series[0].type === 'columnrange'){
                var leftOffset = $(srcChart.container).offset().left;
                var xCoordinate = e.clientX  - srcChart.plotLeft - leftOffset;
                srcTimestamp = srcChart.yAxis[0].toValue(xCoordinate, true)
            }else {
                if (!srcChartPoint || !srcChartPoint.x) return;
                srcTimestamp = srcChartPoint.x;
            }
            for (let i = 0; i < service.highcharts.charts.length; i++) {
                if (!service.highcharts.charts[i]) continue;
                const chart = service.highcharts.charts[i];
                let point;
                //Stategraph's points need to be found in a different way than the normal graphs.
                if(chart.series[0] && chart.series[0].type === 'columnrange') {
                    point = searchPointColumnRange(srcChartPoint, srcTimestamp, chart);
                }
                else {
                    point = searchPoint(srcTimestamp, chart);
                }
                if (point && point.highlight) {
                    if (point.series.onMouseOver) {
                        if (srcChartIndex !== i) point.series.onMouseOver(e);
                    }
                    point.highlight(e, srcChartIndex === i);
                    higlightedPoints.push(point);
                }
                else if (!point){
                    chart.tooltip.hide();
                    chart.xAxis[0].hideCrosshair();
                    chart.yAxis[0].hideCrosshair();
                }
            }
        }

        const throttledHighlight = _.throttle(highlight, 100);
        const mobileThrottledHighlight = _.throttle(highlight, 210);

        function unhighlight() {
            // Prevent delayed hover events from re-highlighting the charts
            throttledHighlight.cancel();
            mobileThrottledHighlight.cancel();

            if (!service.highcharts) return;
            for (let i = 0; i < service.highcharts.charts.length; i++) {
                const chart = service.highcharts.charts[i];
                if (
                    !chart ||
                    chart.renderTo ===
                        this.firstElementChild
                )
                    continue;
                chart.tooltip.hide();
                chart.xAxis[0].hideCrosshair();
                chart.yAxis[0].hideCrosshair();
            }

            _.forEach(higlightedPoints, function (point) {
                if (point.onMouseOut) point.onMouseOut();
                if (point.series && point.series.onMouseOut) point.series.onMouseOut();
            });
            higlightedPoints = [];
        }

        const vSyncContainer = container;

        vSyncContainer.off();
        vSyncContainer.on('mouseleave touchend', '.v-sync-graph .vrm-widget__items--graph', unhighlight);
        vSyncContainer.on('touchstart', '.v-sync-graph .vrm-widget__items--graph', highlight);
        vSyncContainer.on('mousemove', '.v-sync-graph .vrm-widget__items--graph', throttledHighlight);
        vSyncContainer.on('touchmove', '.v-sync-graph .vrm-widget__items--graph', mobileThrottledHighlight);
    }

    get colors() {
        return this.WhitelabelService.getGraphColors();
    }


}

vrmGraphModule.service('GraphService', GraphService);

